/* global window, document, videojs */

import attachAutoplayEvents from './autoplay';
import attachRecommendationEvents from './recommendations';
import { attachStickyPlayerEvents } from './stickyplayer';
import { setQuartileEvents } from './quartiles';

export const eventsToQueue = [
  'ads-ima-adBreakReady', // google.ima.AdEvent.Type.AD_BREAK_READY
  'ads-ima-adCanPlay', // google.ima.AdEvent.Type.AD_CAN_PLAY
  'ads-ima-adMetadata', // google.ima.AdEvent.Type.AD_METADATA
  'ads-ima-allAdsCompleted', // google.ima.AdEvent.Type.ALL_ADS_COMPLETED
  'ads-ima-click', // google.ima.AdEvent.Type.CLICK
  'ads-ima-complete', // google.ima.AdEvent.Type.COMPLETE
  'ads-ima-firstquartile', // google.ima.AdEvent.Type.FIRST_QUARTILE
  'ads-ima-impression', // google.ima.AdEvent.Type.IMPRESSION
  'ads-ima-interaction', // google.ima.AdEvent.Type.INTERACTION
  'ads-ima-loaded', // google.ima.AdEvent.Type.LOADED
  'ads-ima-midpoint', // google.ima.AdEvent.Type.MIDPOINT
  'ads-ima-pause', // google.ima.AdEvent.Type.PAUSED
  'ads-ima-resume', // google.ima.AdEvent.Type.RESUMED
  'ads-ima-skip', // google.ima.AdEvent.Type.SKIPPED
  'ads-ima-start', // google.ima.AdEvent.Type.STARTED
  'ads-ima-thirdquartile', // google.ima.AdEvent.Type.THIRD_QUARTILE
  'ads-ima-viewable_impression', // google.ima.AdEvent.Type.VIEWABLE_IMPRESSION
  'ads-ima-volumeChange', // google.ima.AdEvent.Type.VOLUME_CHANGED
  'ads-ima-mute', // google.ima.AdEvent.Type.VOLUME_MUTED
  'ads-ima-adError', // google.ima.AdErrorEvent.Type.AD_ERROR
  'ads-ima-adRequest',

  'error',
  'adserror',
  'readyforpreroll',
  'adsready',
  'ads-ad-started',
  'view',
  'play',
  'pause',
  'played-q0',
  'played-q1',
  'played-q2',
  'played-q3',
  'played-q4',
  'ended',
  'volumechange',
  'fullscreenchange',
  'autoplayStatus',
];

/**
 * Triggers global event 'hearstPlayer:events:ready'.
 */
function triggerGlobalPlayerEvent() {
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent('hearstPlayer:events:ready', false, false, null);
  window.dispatchEvent(evt);
}

/**
 * Returns player error message if present, empty string otherwise.
 * @param {Object} player - videojs player.
 * @return {string}
 */
function getErrorMessage(player) {
  const error = player.error();
  if (error) {
    return `${error.code}: ${error.message}`;
  }
  return '';
}

/**
 * Returns an object with current player settings to be attached in events.
 * @param {Object} Encore - Encore video player instance.
 * @return {Object}
 */
function getPlayerData(encore) {
  return {
    type: 'video.js',
    duration: Math.floor(encore.player.duration()),
    currentTime: Math.floor(encore.player.currentTime()),
    muted: encore.player.muted(),
    volume: encore.player.volume(),
    autoplay: encore.settings.canAutoplay && encore.shouldAutoplay,
    isFullscreen: encore.player.isFullscreen(),
    width: Math.floor(encore.player.currentWidth()),
    height: Math.floor(encore.player.currentHeight()),
    version: `video.js ${videojs.VERSION}`,
    error: getErrorMessage(encore.player),
  };
}

/**
 * Push events into the global event queue,
 * so that they can be processed at a later time.
 * @param {string} eventName - the name of the event to be fired
 * @param {Object} Encore - Encore video player instance.
 * @param {Object} eventData - optional event data.
 * @param {Object} adEventData - optional ad event data.
 */
function queuePlayerEvent(eventName, encore, eventData, adEventData) {
  if (encore.eventTracker.shouldSkipEvent(eventName, encore.player, encore.source)) {
    return;
  }

  const event = {
    type: eventName,
    embed: encore.source.attributes,
    player: getPlayerData(encore),
    event: eventData,
    adEvent: adEventData,
    adTagUrl: encore.source.adTagUrl,
    timestamp: Date.now(),
  };

  window.hearstPlayer.events.push(event);

  triggerGlobalPlayerEvent();
}

/**
 * Bind all the necessary player events to the queue handler.
 * @param {Object} Encore - Encore video player instance.
 */
export function attachEvents(encore) {
  eventsToQueue.forEach(name => {
    encore.player.on(name, queuePlayerEvent.bind(this,
      name,
      encore));
  });

  if (encore.source.isPlaylist) {
    encore.player.one('play', () => {
      encore.player.autoplay(true);
    });

    encore.player.on('played-q4', () => {
      encore.player.playlist.next();
    });
  }

  encore.player.one('ads-ima-complete', () => {
    encore.player.autoplay(true);
    encore.player.play();
  });

  encore.player.one('ads-ima-skip', () => {
    encore.player.autoplay(true);
    encore.player.play();
  });

  encore.player.one('error', () => {
    setTimeout(() => encore.player.bigPlayButton.hide(), 100);
  });

  setQuartileEvents(encore.player);

  attachAutoplayEvents(encore);

  attachStickyPlayerEvents(encore);

  attachRecommendationEvents(encore);
}



// WEBPACK FOOTER //
// ./src/events.js